'use client';

import React, { useState, useEffect } from 'react';
import {
  BedDouble,
  Users,
  Calendar,
  FlaskConical,
  AlertTriangle,
  Ambulance,
  DollarSign,
  UserCheck,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';



interface KPIData {
  id: string;
  label: string;
  value: string;
  subValue?: string;
  change: number;
  changeLabel: string;
  icon: React.ElementType;
  variant: 'primary' | 'danger' | 'success' | 'warning' | 'default' | 'purple';
  heroCard?: boolean;
  alert?: boolean;
  alertMessage?: string;
  progress?: number;
}

const INITIAL_KPI_DATA: KPIData[] = [
  {
    id: 'kpi-bed-occupancy',
    label: 'Bed Occupancy Rate',
    value: '87%',
    subValue: '261 / 300 beds occupied',
    change: 4.2,
    changeLabel: 'vs yesterday',
    icon: BedDouble,
    variant: 'primary',
    heroCard: true,
    progress: 87,
  },
  {
    id: 'kpi-active-patients',
    label: 'Active Inpatients',
    value: '261',
    subValue: '+14 admitted today',
    change: 5.7,
    changeLabel: 'vs yesterday',
    icon: Users,
    variant: 'default',
  },
  {
    id: 'kpi-appointments',
    label: "Today's Appointments",
    value: '148',
    subValue: '112 completed · 36 pending',
    change: -3.1,
    changeLabel: 'vs yesterday',
    icon: Calendar,
    variant: 'default',
  },
  {
    id: 'kpi-lab-pending',
    label: 'Pending Lab Results',
    value: '47',
    subValue: '12 overdue (>4h)',
    change: 18.2,
    changeLabel: 'vs yesterday',
    icon: FlaskConical,
    variant: 'warning',
    alert: true,
    alertMessage: '12 lab results overdue — action required',
  },
  {
    id: 'kpi-icu',
    label: 'ICU Occupancy',
    value: '94%',
    subValue: '15 / 16 ICU beds',
    change: 6.7,
    changeLabel: 'vs yesterday',
    icon: AlertTriangle,
    variant: 'danger',
    alert: true,
    alertMessage: 'ICU near capacity — 1 bed remaining',
    progress: 94,
  },
  {
    id: 'kpi-emergency',
    label: 'Emergency Admissions',
    value: '23',
    subValue: 'Last 24 hours',
    change: 27.8,
    changeLabel: 'vs prior 24h',
    icon: Ambulance,
    variant: 'danger',
  },
  {
    id: 'kpi-revenue',
    label: 'Revenue Today',
    value: '$84,230',
    subValue: 'Target: $90,000',
    change: 6.4,
    changeLabel: 'vs yesterday',
    icon: DollarSign,
    variant: 'success',
    progress: 94,
  },
  {
    id: 'kpi-doctors',
    label: 'Doctors On Duty',
    value: '38',
    subValue: '6 off-shift in 2h',
    change: 0,
    changeLabel: 'same as yesterday',
    icon: UserCheck,
    variant: 'default',
  },
];

function TrendIcon({ change }: { change: number }) {
  if (change > 0) return <TrendingUp data-component-id="src/app/components/KPIBentoGrid.tsx:127:25" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="127" data-component-end-line="127" data-component-file="KPIBentoGrid.tsx" data-component-name="TrendingUp" data-component-content="%7B%22elementName%22%3A%22TrendingUp%22%7D" size={13} />;
  if (change < 0) return <TrendingDown data-component-id="src/app/components/KPIBentoGrid.tsx:128:25" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="128" data-component-end-line="128" data-component-file="KPIBentoGrid.tsx" data-component-name="TrendingDown" data-component-content="%7B%22elementName%22%3A%22TrendingDown%22%7D" size={13} />;
  return <Minus data-component-id="src/app/components/KPIBentoGrid.tsx:129:9" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="129" data-component-end-line="129" data-component-file="KPIBentoGrid.tsx" data-component-name="Minus" data-component-content="%7B%22elementName%22%3A%22Minus%22%7D" size={13} />;
}

const VARIANT_STYLES: Record<string, { bg: string; iconBg: string; iconColor: string; valueCls: string }> = {
  primary: {
    bg: 'bg-card border border-border',
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    valueCls: 'text-foreground',
  },
  danger: {
    bg: 'bg-danger-muted border border-danger/20',
    iconBg: 'bg-danger/10',
    iconColor: 'text-danger',
    valueCls: 'text-danger',
  },
  success: {
    bg: 'bg-success-muted border border-success/20',
    iconBg: 'bg-success/10',
    iconColor: 'text-success',
    valueCls: 'text-success',
  },
  warning: {
    bg: 'bg-warning-muted border border-warning/20',
    iconBg: 'bg-warning/10',
    iconColor: 'text-warning',
    valueCls: 'text-warning',
  },
  default: {
    bg: 'bg-card border border-border',
    iconBg: 'bg-muted',
    iconColor: 'text-muted-foreground',
    valueCls: 'text-foreground',
  },
  purple: {
    bg: 'bg-purple-muted border border-purple/20',
    iconBg: 'bg-purple/10',
    iconColor: 'text-purple',
    valueCls: 'text-purple',
  },
};

interface KPICardProps {
  data: KPIData;
  hero?: boolean;
}

function KPICard({ data, hero = false }: KPICardProps) {
  const styles = VARIANT_STYLES[data.variant] ?? VARIANT_STYLES.default;
  const CardIcon = data.icon;
  const isPositive = data.change > 0;
  const isNeutral = data.change === 0;

  const trendColor = isNeutral
    ? 'text-muted-foreground bg-muted'
    : data.variant === 'danger' || data.variant === 'warning'
    ? isPositive
      ? 'text-danger bg-danger-muted' :'text-success bg-success-muted'
    : isPositive
    ? 'text-success bg-success-muted' :'text-danger bg-danger-muted';

  return (
    <div data-component-id="src/app/components/KPIBentoGrid.tsx:191:4" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="191" data-component-end-line="242" data-component-file="KPIBentoGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`${styles.bg} rounded-xl p-5 shadow-card card-hover relative overflow-hidden ${hero ? 'md:col-span-2' : ''}`}>
      {/* Alert banner */}
      {data.alert && data.alertMessage && (
        <div data-component-id="src/app/components/KPIBentoGrid.tsx:194:8" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="194" data-component-end-line="197" data-component-file="KPIBentoGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-0%20left-0%20right-0%20bg-danger%2F90%20text-white%20text-%5B10px%5D%20font-600%20px-3%20py-1%20flex%20items-center%20gap-1.5%22%7D" className="absolute top-0 left-0 right-0 bg-danger/90 text-white text-[10px] font-600 px-3 py-1 flex items-center gap-1.5">
          <AlertTriangle data-component-id="src/app/components/KPIBentoGrid.tsx:195:10" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="195" data-component-end-line="195" data-component-file="KPIBentoGrid.tsx" data-component-name="AlertTriangle" data-component-content="%7B%22elementName%22%3A%22AlertTriangle%22%7D" size={10} />
          {data.alertMessage}
        </div>
      )}

      <div data-component-id="src/app/components/KPIBentoGrid.tsx:200:6" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="200" data-component-end-line="241" data-component-file="KPIBentoGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={data.alert ? 'pt-5' : ''}>
        <div data-component-id="src/app/components/KPIBentoGrid.tsx:201:8" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="201" data-component-end-line="208" data-component-file="KPIBentoGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20justify-between%20mb-3%22%7D" className="flex items-start justify-between mb-3">
          <p data-component-id="src/app/components/KPIBentoGrid.tsx:202:10" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="202" data-component-end-line="204" data-component-file="KPIBentoGrid.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-%5B12px%5D%20font-600%20text-muted-foreground%20uppercase%20tracking-wide%20leading-tight%22%7D" className="text-[12px] font-600 text-muted-foreground uppercase tracking-wide leading-tight">
            {data.label}
          </p>
          <div data-component-id="src/app/components/KPIBentoGrid.tsx:205:10" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="205" data-component-end-line="207" data-component-file="KPIBentoGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${styles.iconBg}`}>
            <CardIcon data-component-id="src/app/components/KPIBentoGrid.tsx:206:12" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="206" data-component-end-line="206" data-component-file="KPIBentoGrid.tsx" data-component-name="CardIcon" data-component-content="%7B%22elementName%22%3A%22CardIcon%22%7D" size={18} className={styles.iconColor} />
          </div>
        </div>

        <div data-component-id="src/app/components/KPIBentoGrid.tsx:210:8" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="210" data-component-end-line="214" data-component-file="KPIBentoGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-1%22%7D" className="mb-1">
          <span data-component-id="src/app/components/KPIBentoGrid.tsx:211:10" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="211" data-component-end-line="213" data-component-file="KPIBentoGrid.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D" className={`font-tabular font-700 ${styles.valueCls} ${hero ? 'text-4xl' : 'text-3xl'}`}>
            {data.value}
          </span>
        </div>

        {data.subValue && (
          <p data-component-id="src/app/components/KPIBentoGrid.tsx:217:10" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="217" data-component-end-line="217" data-component-file="KPIBentoGrid.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-%5B12px%5D%20text-muted-foreground%20mb-3%22%7D" className="text-[12px] text-muted-foreground mb-3">{data.subValue}</p>
        )}

        {/* Progress bar */}
        {data.progress !== undefined && (
          <div data-component-id="src/app/components/KPIBentoGrid.tsx:222:10" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="222" data-component-end-line="232" data-component-file="KPIBentoGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-3%22%7D" className="mb-3">
            <div data-component-id="src/app/components/KPIBentoGrid.tsx:223:12" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="223" data-component-end-line="231" data-component-file="KPIBentoGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-1.5%20bg-black%2F10%20rounded-full%20overflow-hidden%22%7D" className="h-1.5 bg-black/10 rounded-full overflow-hidden">
              <div data-component-id="src/app/components/KPIBentoGrid.tsx:224:14" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="224" data-component-end-line="230" data-component-file="KPIBentoGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                className={`h-full rounded-full transition-all duration-500 ${
                  data.progress >= 90 ? 'bg-danger' :
                  data.progress >= 75 ? 'bg-warning' : 'bg-success'
                }`}
                style={{ width: `${data.progress}%` }}
              />
            </div>
          </div>
        )}

        <div data-component-id="src/app/components/KPIBentoGrid.tsx:235:8" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="235" data-component-end-line="240" data-component-file="KPIBentoGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-600 ${trendColor}`}>
          <TrendIcon data-component-id="src/app/components/KPIBentoGrid.tsx:236:10" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="236" data-component-end-line="236" data-component-file="KPIBentoGrid.tsx" data-component-name="TrendIcon" data-component-content="%7B%22elementName%22%3A%22TrendIcon%22%7D" change={data.change} />
          <span data-component-id="src/app/components/KPIBentoGrid.tsx:237:10" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="237" data-component-end-line="239" data-component-file="KPIBentoGrid.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">
            {isNeutral ? 'No change' : `${isPositive ? '+' : ''}${data.change}%`} {data.changeLabel}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function KPIBentoGrid() {
  const [kpiData, setKpiData] = useState(INITIAL_KPI_DATA);

  // Backend integration point: replace with WebSocket or polling endpoint
  // e.g. useEffect(() => { const ws = new WebSocket('/api/ws/kpi'); ... }, []);
  useEffect(() => {
    const interval = setInterval(() => {
      setKpiData((prev) =>
        prev.map((item) => {
          if (item.id === 'kpi-active-patients') {
            const delta = Math.random() > 0.5 ? 1 : 0;
            const current = parseInt(item.value) + delta;
            return { ...item, value: current.toString() };
          }
          return item;
        })
      );
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  // Grid plan: 8 cards → grid-cols-4
  // Row 1: hero (col-span-2) + 2 regular = 4 columns
  // Row 2: 4 regular cards
  const [heroCard, ...restCards] = kpiData;
  const row1Rest = restCards.slice(0, 2);
  const row2 = restCards.slice(2, 6);

  return (
    <div data-component-id="src/app/components/KPIBentoGrid.tsx:275:4" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="275" data-component-end-line="291" data-component-file="KPIBentoGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
      {/* Row 1: hero + 2 */}
      <div data-component-id="src/app/components/KPIBentoGrid.tsx:277:6" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="277" data-component-end-line="284" data-component-file="KPIBentoGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-4%20xl%3Agrid-cols-4%202xl%3Agrid-cols-4%20gap-4%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
        <div data-component-id="src/app/components/KPIBentoGrid.tsx:278:8" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="278" data-component-end-line="280" data-component-file="KPIBentoGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22md%3Acol-span-2%22%7D" className="md:col-span-2">
          <KPICard data-component-id="src/app/components/KPIBentoGrid.tsx:279:10" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="279" data-component-end-line="279" data-component-file="KPIBentoGrid.tsx" data-component-name="KPICard" data-component-content="%7B%22elementName%22%3A%22KPICard%22%7D" data={heroCard} hero />
        </div>
        {row1Rest.map((item) => (
          <KPICard data-component-id="src/app/components/KPIBentoGrid.tsx:282:10" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="282" data-component-end-line="282" data-component-file="KPIBentoGrid.tsx" data-component-name="KPICard" data-component-content="%7B%22elementName%22%3A%22KPICard%22%7D" key={item.id} data={item} />
        ))}
      </div>
      {/* Row 2: 4 regular */}
      <div data-component-id="src/app/components/KPIBentoGrid.tsx:286:6" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="286" data-component-end-line="290" data-component-file="KPIBentoGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-4%20xl%3Agrid-cols-4%202xl%3Agrid-cols-4%20gap-4%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
        {row2.map((item) => (
          <KPICard data-component-id="src/app/components/KPIBentoGrid.tsx:288:10" data-component-path="src/app/components/KPIBentoGrid.tsx" data-component-line="288" data-component-end-line="288" data-component-file="KPIBentoGrid.tsx" data-component-name="KPICard" data-component-content="%7B%22elementName%22%3A%22KPICard%22%7D" key={item.id} data={item} />
        ))}
      </div>
    </div>
  );
}